import { ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import { lockBodyScroll } from '@/lib/bodyScrollLock';

export interface ModalOrigin {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  /** Source rect to "grow" the modal from. Falls back to a centred punch-in. */
  origin?: ModalOrigin | null;
  /** Waiter notebook: show the dialog at once — no grow-from-the-card, no
   *  punch-in. The waiter opens one modal per dish and aims for the footer
   *  button while it is still moving; 440ms of travel buys mistaps, not
   *  delight. Guests keep the animation. */
  instant?: boolean;
  /** Where the dialog sits inside the viewport. `'center'` (default) keeps
   * every existing caller byte-identical. `'top'` anchors it near the top
   * on phones — iOS does not shrink the layout viewport when the on-screen
   * keyboard opens, so a vertically centred box (with its submit button
   * below the fold) ends up hidden under the keyboard; `top` keeps it
   * clear. Reverts to centred at md+ (tablet), where there's room either
   * way.
   * 2026-08-28 (spec waiter-salle-ux D11) — `NoteCreateModals` dropped its
   * `anchor="top"` in favour of `index.html`'s
   * `interactive-widget=resizes-content`, which shrinks the visual viewport
   * instead. No caller passes `'top'` any more; the value and the prop stay
   * so a future keyboard-avoidance need doesn't have to reinvent this. */
  anchor?: 'center' | 'top';
  /** Waiter creation modals (04-ux-mobile-spec.md §1a: « le tap sur le fond
   *  ne fait rien ») — a stray tap on the scrim while typing a table number
   *  must not throw the input away. Guests keep tap-to-close. Escape and the
   *  modal's own ✕ / Annuler always close, whatever this says. */
  dismissOnBackdrop?: boolean;
  /** Le voile teinté + flouté du fond. `false` quand une AUTRE modale est
   *  ouverte par-dessus celle-ci et pose déjà le sien : deux
   *  `backdrop-blur` plein écran empilés, c'est deux fois le même travail de
   *  composition à chaque image, sur le téléphone d'un serveur en plein
   *  service. La boîte, elle, reste rendue — c'est ce qui garde la fenêtre
   *  du dessous visible derrière celle du dessus. */
  scrim?: boolean;
  children: ReactNode;
}

export default function Modal({
  open,
  onClose,
  ariaLabel,
  origin,
  instant = false,
  anchor = 'center',
  dismissOnBackdrop = true,
  scrim = true,
  children,
}: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const releaseScroll = lockBodyScroll();
    return () => {
      document.removeEventListener('keydown', onKey);
      releaseScroll();
    };
  }, [open, onClose]);

  // FLIP: measure dialog at its centred resting position, then jump to the
  // origin's translate+scale and animate back to identity.
  useLayoutEffect(() => {
    if (!open || !origin || instant) return;
    const el = dialogRef.current;
    if (!el) return;

    // Clear any inline transform from a previous run (StrictMode runs effects
    // twice in dev) so we measure the dialog at its true resting size.
    el.style.transition = 'none';
    el.style.transform = '';
    el.style.opacity = '';

    const mr = el.getBoundingClientRect();
    const ocx = origin.x + origin.width / 2;
    const ocy = origin.y + origin.height / 2;
    const mcx = mr.left + mr.width / 2;
    const mcy = mr.top + mr.height / 2;
    const dx = ocx - mcx;
    const dy = ocy - mcy;
    const sx = origin.width / mr.width;
    const sy = origin.height / mr.height;

    // Slight tilt for character — direction picked from the click's horizontal
    // offset so cards on the left lean left and cards on the right lean right.
    const tilt = ocx < window.innerWidth / 2 ? -8 : 8;

    // 1) Paint the start frame: dialog sits exactly at the source card.
    el.style.animation = 'none';
    el.style.transition = 'none';
    el.style.transformOrigin = 'center center';
    el.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy}) rotate(${tilt}deg)`;
    el.style.opacity = '0';

    // 2) Next frame: arm the transition and let it ride back to identity.
    const raf = requestAnimationFrame(() => {
      el.style.transition = 'transform 440ms cubic-bezier(0.22, 1, 0.36, 1), opacity 280ms ease-out';
      el.style.transform = '';
      el.style.opacity = '1';
    });

    const cleanup = window.setTimeout(() => {
      el.style.transition = '';
      el.style.transform = '';
      el.style.opacity = '';
    }, 540);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(cleanup);
    };
  }, [open, origin, instant]);

  if (!open) return null;
  const anchorClasses =
    anchor === 'top'
      ? 'items-start justify-center px-3 pb-3 pt-[max(24px,calc(env(safe-area-inset-top)+16px))] md:items-center md:p-6'
      : 'items-center justify-center p-3 md:p-6';
  return (
    <div
      data-anchor={anchor}
      className={`fixed inset-0 z-[1100] flex ${anchorClasses} ${
        scrim ? 'bg-ink/75 backdrop-blur-[6px]' : ''
      } ${instant ? '' : 'animate-[modalOverlay_180ms_ease-out]'}`}
    >
      <div className="absolute inset-0" onClick={dismissOnBackdrop ? onClose : undefined} aria-hidden="true" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={`relative bg-paper border-2 border-ink max-w-[640px] w-full max-h-[calc(100dvh-24px)] md:max-h-[calc(100dvh-48px)] flex flex-col overflow-hidden ${
          origin || instant ? '' : 'animate-[modalPunchIn_280ms_cubic-bezier(0.34,1.6,0.5,1)]'
        }`}
        style={{ boxShadow: 'var(--shadow-stamp-lg), 0 30px 60px -20px rgba(0,0,0,0.35)' }}
      >
        {children}
      </div>
      <style>{`
        @keyframes modalOverlay {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
